import React from 'react';
import { ClientTranscript } from '../../types/client';
import { formatDatetime, getSentimentBadge } from '../../utils/clientUtils';
import NotesSection from '../NotesSection';

interface TranscriptDetailsProps {
  selectedTranscript: ClientTranscript | null;
}

const TranscriptDetails: React.FC<TranscriptDetailsProps> = ({
  selectedTranscript
}) => {
  if (!selectedTranscript) {
    return (
      <div className="flex-1 bg-white flex flex-col">
        <div className="flex items-center justify-center h-full p-6 text-center">
          <div>
            <div className="text-gray-400 text-4xl mb-4">📄</div>
            <p className="text-gray-600">Select a transcript to view details</p>
            <p className="text-gray-500 text-sm mt-2">
              Choose from the transcript list to see the full conversation and AI analysis
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Check if relationship notes should be shown (not auto-detected)
  const shouldShowRelationshipNotes = selectedTranscript.relationship_notes && 
    !selectedTranscript.relationship_notes.includes('Auto-detected from transcript content');

  return (
    <div className="flex-1 bg-white flex flex-col">
      {/* Transcript Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {selectedTranscript.title}
            </h2>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <span>📅 {formatDatetime(selectedTranscript.created_at)}</span>
              <span>📝 {selectedTranscript.word_count} words</span>
              {selectedTranscript.sentiment_label && (
                <span
                  className={`px-2 py-1 text-xs font-medium rounded-full ${getSentimentBadge(
                    selectedTranscript.sentiment_label
                  )}`}
                >
                  {selectedTranscript.sentiment_label}{' '}
                  {selectedTranscript.sentiment_score &&
                    `(${Math.round(selectedTranscript.sentiment_score * 100)}%)`}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Transcript Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="prose max-w-none">
          <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
            {selectedTranscript.content}
          </div>
        </div>

        {/* Regular Notes Section */}
        <div className="mt-6">
          <NotesSection transcriptId={selectedTranscript.transcript_id} />
        </div>

        {/* Simple Client Relationship Notes - Only if not auto-detected */}
        {shouldShowRelationshipNotes && (
          <div className="mt-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-700">{selectedTranscript.relationship_notes}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TranscriptDetails; 